import React, { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";
import { api, formatINR } from "@/lib/api";
import { Link } from "react-router-dom";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Sparkles, Save, Trash2, Wand2, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "@/lib/cart";
import RecommendationStrip from "@/components/RecommendationStrip";

const OCCASIONS = ["wedding", "reception", "business", "casual", "festival"];

export default function VirtualWardrobe() {
  const { user, loading } = useAuth();
  const cart = useCart();
  const [orders, setOrders] = useState({ orders: [], bespoke: [] });
  const [looks, setLooks] = useState([]);
  const [auto, setAuto] = useState(null);
  const [occasion, setOccasion] = useState("business");
  const [name, setName] = useState("My new look");

  const refresh = async () => {
    if (!user) return;
    const [a, b] = await Promise.all([api.get("/orders"), api.get("/wardrobe/looks")]);
    setOrders(a.data); setLooks(b.data);
  };
  useEffect(() => { refresh(); }, [user]);

  const build = async () => { try { const { data } = await api.get(`/wardrobe/auto-build?occasion=${occasion}`); setAuto(data); } catch { toast.error("Failed"); } };

  const saveAuto = async () => {
    if (!auto?.pieces?.length) return;
    try {
      await api.post("/wardrobe/looks", { name, product_ids: auto.pieces.map((p) => p.id), occasion });
      toast.success("Look saved");
      setAuto(null); refresh();
    } catch { toast.error("Failed"); }
  };

  const addLookToCart = async (look) => {
    for (const p of look.products) { try { await cart.add(p.id, { quantity: 1, size: p.sizes?.[0], color: p.colors?.[0] }); } catch {} }
    toast.success(`Added ${look.products.length} pieces`);
  };

  const deleteLook = async (lid) => { await api.delete(`/wardrobe/looks/${lid}`); toast("Deleted"); refresh(); };

  if (loading) return <div className="sq-container py-20 sq-eyebrow">Loading wardrobe…</div>;
  if (!user) return <div className="sq-container py-20"><Link to="/auth" className="sq-btn-primary">Sign in</Link></div>;

  const allItems = [
    ...orders.orders.flatMap((o) => o.items.map((it) => ({ ...it.product_snapshot, id: it.product_id, _from: o.id }))),
    ...orders.bespoke.map((b) => ({ name: `Bespoke ${b.garment_type}`, image: "", id: b.id, category: "bespoke", _from: b.id })),
  ];

  return (
    <main className="sq-container py-12" data-testid="wardrobe-page">
      <div className="sq-eyebrow text-squire-jeans">Your Closet</div>
      <h1 className="font-display text-5xl md:text-6xl mt-2">Virtual Wardrobe</h1>
      <p className="text-[#4B5563] mt-3 max-w-xl">Every piece you've owned, every outfit you might compose — in one place.</p>

      <Tabs defaultValue="closet" className="mt-10">
        <TabsList>
          <TabsTrigger value="closet" data-testid="ward-tab-closet">Closet</TabsTrigger>
          <TabsTrigger value="builder" data-testid="ward-tab-builder">Outfit Builder</TabsTrigger>
          <TabsTrigger value="looks" data-testid="ward-tab-looks">Saved Looks</TabsTrigger>
        </TabsList>

        <TabsContent value="closet" className="mt-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
            {allItems.length === 0 ? <div className="col-span-full text-[#4B5563]">Nothing here yet — your first order will appear automatically.</div> : allItems.map((it, i) => (
              <div key={i} className="border border-black/10" data-testid={`closet-item-${i}`}>
                {it.image && <img src={it.image} alt="" className="aspect-[3/4] w-full object-cover" />}
                <div className="p-3">
                  <div className="font-display text-sm">{it.name}</div>
                  <div className="sq-eyebrow text-[10px] mt-1">{it.category} · {it._from}</div>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="builder" className="mt-6 space-y-6">
          <div className="border border-black/10 p-6">
            <div className="sq-eyebrow flex items-center gap-2"><Wand2 size={12} /> Auto Outfit Builder</div>
            <div className="flex flex-wrap items-end gap-3 mt-4">
              <label className="block"><span className="sq-eyebrow text-[10px]">Occasion</span>
                <select data-testid="ward-occasion" value={occasion} onChange={(e) => setOccasion(e.target.value)} className="bg-transparent border-b border-black/30 outline-none py-1">
                  {OCCASIONS.map((o) => <option key={o}>{o}</option>)}
                </select>
              </label>
              <button data-testid="ward-build" onClick={build} className="sq-btn-primary"><Sparkles size={14} /> Build a look</button>
            </div>
          </div>
          {auto && (
            <div className="border border-squire-jeans p-6" data-testid="ward-auto-result">
              <div className="font-display text-2xl">A suggested {occasion} look</div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                {auto.pieces.map((p) => (
                  <div key={p.id} className="border border-black/10" data-testid={`ward-piece-${p.id}`}>
                    <img src={p.images?.[0]} alt={p.name} className="w-full aspect-[3/4] object-cover" />
                    <div className="p-3">
                      <div className="font-display text-sm">{p.name}</div>
                      <div className="sq-eyebrow text-[10px]">{p._source === "wardrobe" ? "From wardrobe" : "Suggested"} · {formatINR(p.price)}</div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-5 flex flex-wrap gap-3 items-center">
                <input data-testid="ward-look-name" value={name} onChange={(e) => setName(e.target.value)} className="flex-1 bg-transparent border-b border-black/30 outline-none py-1" />
                <button data-testid="ward-look-save" onClick={saveAuto} className="sq-btn-primary"><Save size={14} /> Save Look</button>
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="looks" className="mt-6 space-y-4">
          {looks.length === 0 && <div className="text-sm text-[#4B5563]">No saved looks yet.</div>}
          {looks.map((lk) => (
            <div key={lk.id} className="border border-black/10 p-5" data-testid={`look-${lk.id}`}>
              <div className="flex justify-between flex-wrap gap-2">
                <div>
                  <div className="font-display text-xl">{lk.name}</div>
                  <div className="sq-eyebrow text-[10px] mt-1">{lk.id} · {lk.occasion || "—"} · {lk.products?.length} pieces</div>
                </div>
                <div className="flex gap-2">
                  <button data-testid={`look-cart-${lk.id}`} onClick={() => addLookToCart(lk)} className="sq-btn-outline !py-1 !px-3 !text-[10px]"><ShoppingBag size={12} /> Add all</button>
                  <button data-testid={`look-delete-${lk.id}`} onClick={() => deleteLook(lk.id)} className="text-xs text-[#4B5563] hover:text-squire-jeans"><Trash2 size={12} /></button>
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
                {lk.products?.map((p) => (
                  <Link key={p.id} to={`/product/${p.id}`} className="block">
                    <img src={p.images?.[0]} alt={p.name} className="w-full aspect-[3/4] object-cover" />
                    <div className="font-display text-xs mt-1">{p.name}</div>
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </TabsContent>
      </Tabs>

      <RecommendationStrip context="wardrobe" title="To complete your wardrobe" testid="wardrobe-recs" />
    </main>
  );
}
